import { useEffect, useRef, useState } from "hono/jsx";
import { HUD } from "./HUD.tsx";
import { Menu } from "./Menu.tsx";
import { PlayerList } from "./PlayerList.tsx";
import { Inventory } from "./Inventory.tsx";
import { useVoxholeConnection } from "./useVoxholeConnection.ts";

interface VoxholeRoomProps {
  roomId: string;
  user?: { id: string; isAnonymous?: boolean };
}

export function VoxholeRoom({ roomId, user }: VoxholeRoomProps) {
  const {
    connected,
    roomNotFound,
    setRoomNotFound,
    initData,
    isHost,
    isDuplicate,
    banMessage,
    wsRef,
    setRetryCount,
  } = useVoxholeConnection({ roomId, user });

  const containerRef = useRef<HTMLDivElement>(null);
  // deno-lint-ignore no-explicit-any
  const voxholeInstanceRef = useRef<any>(null);
  const voxholeInitRef = useRef(false);
  const initTimerRef = useRef<number | undefined>(undefined);

  // Destroy on invalidation
  useEffect(() => {
    if (roomNotFound || !connected) {
      if (voxholeInstanceRef.current) {
        voxholeInstanceRef.current.destroy();
        voxholeInstanceRef.current = null;
        voxholeInitRef.current = false;
      }
    }
  }, [roomNotFound, connected]);

  // Initialize when ready
  useEffect(() => {
    if (
      !voxholeInitRef.current && containerRef.current && initData &&
      wsRef.current && connected && !roomNotFound
    ) {
      voxholeInitRef.current = true;
      initVoxhole(wsRef.current, initData);
    }
  }, [initData, roomNotFound, connected]);

  // deno-lint-ignore no-explicit-any
  const initVoxhole = (ws: WebSocket, initData: any) => {
    if (voxholeInstanceRef.current) return;

    // deno-lint-ignore no-explicit-any
    (globalThis as any).__voxholeSocket = ws;
    // deno-lint-ignore no-explicit-any
    (globalThis as any).__voxholeInitData = initData;

    if (!user?.id && initData.yourId) {
      try {
        sessionStorage.setItem("vox:guestId", initData.yourId);
      } catch (_e) {
        // Ignore session storage errors
      }
    }

    const check = () => {
      // deno-lint-ignore no-explicit-any
      if ((globalThis as any).initVoxhole && containerRef.current) {
        if (voxholeInstanceRef.current) return;
        // deno-lint-ignore no-explicit-any
        voxholeInstanceRef.current = (globalThis as any).initVoxhole(
          containerRef.current,
          roomId,
        );
      } else {
        initTimerRef.current = setTimeout(check, 50);
      }
    };
    check();
  };

  const handleCreate = async () => {
    try {
      const res = await fetch("/api/voxhole/rooms", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customId: roomId, isPublic: true }),
      });
      if (res.ok) {
        setRoomNotFound(false);
        setRetryCount((c) => c + 1);
      }
    } catch (_err) {
      // Ignore fetch errors
    }
  };

  if (isDuplicate) return null;

  const [isUnderwater, setIsUnderwater] = useState(false);
  const [inventoryOpen, setInventoryOpen] = useState(false);

  useEffect(() => {
    const onWaterUpdate = ((e: CustomEvent) => {
      setIsUnderwater(e.detail);
    }) as EventListener;
    document.addEventListener("vox:water:update", onWaterUpdate);
    return () =>
      document.removeEventListener("vox:water:update", onWaterUpdate);
  }, []);

  // Listen for inventory toggle
  useEffect(() => {
    const onInventoryToggle = ((e: CustomEvent) => {
      setInventoryOpen(e.detail);
    }) as EventListener;
    document.addEventListener("vox:inventory:toggle", onInventoryToggle);
    return () =>
      document.removeEventListener("vox:inventory:toggle", onInventoryToggle);
  }, []);

  if (roomNotFound) {
    const canCreate = user && !user.isAnonymous;
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-base-200 p-4 space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-4xl font-black text-primary tracking-tighter uppercase">
            Room Not Found
          </h2>
          <p className="opacity-60 font-bold italic">
            The room code "{roomId}" doesn't exist yet.
          </p>
        </div>
        <div className="card w-full max-w-sm bg-base-100 shadow-2xl overflow-hidden">
          <div className="card-body items-center text-center space-y-4">
            {canCreate
              ? (
                <>
                  <div className="text-xs uppercase font-black opacity-30 tracking-widest">
                    Wanna start it?
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary btn-lg w-full font-black uppercase text-xl h-20 shadow-xl"
                    onClick={handleCreate}
                  >
                    Create Room {roomId}
                  </button>
                </>
              )
              : (
                <>
                  <div className="text-xs uppercase font-black opacity-30 tracking-widest">
                    Login Required
                  </div>
                  <div className="p-4 bg-base-200 rounded-lg w-full">
                    <p className="text-sm opacity-70">
                      You need to be logged in to create rooms.
                    </p>
                  </div>
                  <a
                    href="/login"
                    className="btn btn-primary w-full font-black uppercase"
                  >
                    Login
                  </a>
                </>
              )}
            <a href="/" className="btn btn-ghost btn-sm opacity-50 font-black">
              Back Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (banMessage) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-black text-white px-4 space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-6xl font-black text-red-500 tracking-tighter uppercase">
            BANNED
          </h2>
          <p className="opacity-60 font-bold italic max-w-md">{banMessage}</p>
        </div>
        <a
          href="/voxhole"
          className="px-6 py-3 rounded bg-white/10 hover:bg-white/20 font-bold transition-colors"
        >
          Back to Lobby
        </a>
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-base-300">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 w-full h-full overflow-hidden bg-black">
      <div ref={containerRef} className="w-full h-full"></div>

      {/* Underwater Overlay */}
      <div
        className="pointer-events-none fixed inset-0 bg-blue-900 z-40 transition-opacity duration-500"
        style={{ opacity: isUnderwater ? 0.4 : 0 }}
      />

      <div className="relative z-50">
        <HUD roomId={roomId} />
        <PlayerList />
        <Menu
          isHost={isHost}
          isPublic={initData?.isPublic}
        />
        <Inventory isOpen={inventoryOpen} />
      </div>
    </div>
  );
}
